import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { PDF_COLORS, PDF_FONT_SIZE } from './constants';
// Create styles
const styles = StyleSheet.create({
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 0.2,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: PDF_COLORS.BORDER,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomColor: PDF_COLORS.BORDER,
    minHeight: 24,
  },
  tableSummaryRow: {
    flexDirection: 'row',
    borderBottomColor: PDF_COLORS.BORDER,
    backgroundColor: PDF_COLORS.HEADER_BG,
    minHeight: 24,
  },
  tableHeader: {
    backgroundColor: PDF_COLORS.HEADER_BG,
    fontSize: PDF_FONT_SIZE.TITLE,

  },
  tableCol: {
    width: '25%',
    borderStyle: 'solid',
    borderWidth: 0.2,
    borderTopWidth: 0,
    flexGrow: 1,
  },
  tableCell: {
    margin: 5,
    fontSize: PDF_FONT_SIZE.TITLE,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },

  infoDetails: {
    display: 'flex',
    flexDirection: 'row',
  },

  headerTableSection: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: PDF_COLORS.HEADER_BG,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderStyle: 'solid',
    borderWidth: 0.4,
    borderColor: PDF_COLORS.BORDER,
  },
  tableFooterData: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  footerContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  w100: {
    width: '100%',
  },
});

/**
 * Renders a table component using @react-pdf/renderer.
 *
 * @param {Object[]} data - Array of data objects to be displayed in the table.
 * @param {Object[{
 * title: string,
 * dataIndex: string,
 * }]} headers - Array of header objects for the table. Each header must have a `dataIndex` field to relate to the key of the records in the `data` prop.
 * @param {React.ReactNode} headerSection - A React node to be displayed in the header section of the table.
 *
 * @returns {React.ReactElement} The rendered table component.
 */
export default function TablePDF({
  data = [],
  headers = [],
  headerSection = [],
  summary = [],
  footer = [],
}) {
  // Rules colors - glosary
  const COLORS = {
    true: '#52c41a',
    false: '#efd005',
  };

  return (
    <View>
      <View style={styles.headerTableSection}>{headerSection}</View>
      <View style={styles.table}>
        {/* Table Header */}
        <View style={[styles.tableRow, styles.tableHeader]}>
          {headers.map(header => (
            <View style={styles.tableCol} key={header.title}>
              <Text style={styles.tableCell}>{header.title}</Text>
            </View>
          ))}
        </View>
        {/* Table Body */}
        {data.map((record, i) => (
          <View wrap={false} style={styles.tableRow} key={i}>
            {headers.map(header => (
              <View
                style={[
                  styles.tableCol,
                  { color: COLORS[record[`${header.dataIndex}_changed`]] ?? null },
                ]}
                key={header.dataIndex}
              >
                <Text style={styles.tableCell}>{record[header.dataIndex]}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>
      {/* Summary */}
      {summary.length > 0 && (
        <View style={styles.tableSummaryRow}>
          {summary.map((item, index) => (
            <View style={styles.tableCol} key={index}>
              <Text style={styles.tableCell}>{item}</Text>
            </View>
          ))}
        </View>
      )}
      {/* Footer */}
      {
        <View style={[styles.footerContainer]}>
          {footer.map(item => (
            <View style={[styles.tableFooterData, styles.tableCol, styles.w100]} key={item.label}>
              <Text style={styles.tableCell}>{item.label}</Text>
              <Text style={styles.tableCell}>{item.value}</Text>
            </View>
          ))}
        </View>
      }
    </View>
  );
}
